import {useCallback, useMemo} from 'react';
import {createSelector} from 'reselect';

import {useMemoizedSelector} from '../../../state/hooks';
import {useRunsData, useRunSetsQuery} from '../../../state/runs/hooks';
import * as FilterActions from '../../../state/views/filter/actions';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelActions from '../../../state/views/panel/actions';
import type * as PanelTypes from '../../../state/views/panel/types';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {type RootState} from '../../../types/redux';
import {type Panel, transformQuery} from '../../../util/panelTypes';
import {makeRunSetFiltersRefSelector} from '../../RunSelector/runSetSelectors';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {usePanelWithInheritedSettings} from '../../WorkspaceDrawer/Settings/hooks/usePanelInheritedSettings';
import {usePanelContext} from '../PanelContextProvider';
import {useInheritedSettings} from './useInheritedSettings';

const makePanelIsInSectionSelector = (
  sectionRef: PanelBankSectionConfigTypes.Ref | undefined,
  panelRef: PanelTypes.Ref
) =>
  createSelector(
    (state: RootState) =>
      sectionRef == null
        ? undefined
        : state.views.parts[sectionRef.type]?.[sectionRef.id]?.panelRefs,
    panelRefs =>
      sectionRef == null ||
      panelRefs == null ||
      panelRefs.some(ref => ref.id === panelRef.id)
  );

export const usePanelSharedProps = (panelRef: PanelTypes.Ref) => {
  const {panelBankSectionConfigRef} = usePanelContext();
  const inheritedSettings = useInheritedSettings();
  const panel = usePanelWithInheritedSettings(panelRef, inheritedSettings);
  return usePanelSharedPropsForPanel(
    panelRef,
    panel,
    panelBankSectionConfigRef
  );
};

/**
 * Same as `usePanelSharedProps`, but takes the resolved panel and section ref
 * directly instead of reading them from PanelContext, so callers (e.g. media
 * panels) can avoid subscribing to PanelContext entirely.
 */
export const usePanelSharedPropsForPanel = (
  panelRef: PanelTypes.Ref,
  panel: Panel,
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref | undefined
) => {
  const {
    customRunColorsRef,
    customRunNamesRef,
    panelBankConfigRef,
    runSetRefs,
    semanticLegendRef,
  } = usePanelsSharedRefsContext();

  const panelIsInSection = useMemoizedSelector(
    makePanelIsInSectionSelector,
    panelBankSectionConfigRef,
    panelRef
  );
  // Editor copies are staging state. Making each dropdown edit undoable rebuilds
  // inverse state for the temporary panel and makes Apply pay for it again.
  const isDetachedPanelCopy =
    panelBankSectionConfigRef != null && !panelIsInSection;

  const updatePanelConfig = useCallback(
    (
      ref: PanelTypes.Ref,
      configUpdate: Partial<PanelTypes.Panel['config']>,
      options?: {isUndoable?: boolean}
    ) =>
      PanelActions.updateConfig(
        ref,
        configUpdate,
        panelBankSectionConfigRef,
        panelBankConfigRef,
        isDetachedPanelCopy ? {...options, isUndoable: false} : options
      ),
    [panelBankSectionConfigRef, panelBankConfigRef, isDetachedPanelCopy]
  );
  const updateConfig = ViewHooks.useViewAction(panelRef, updatePanelConfig);

  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const semanticLegendSettings = ViewHooks.usePartMaybe(semanticLegendRef);

  // Use useMemoizedSelector directly with the selector factory instead of the
  // convenience hooks in RunSelectorRunSetContext (e.g., usePinnedAndBaseline).
  // This usePanelSharedProps hook is called from the reports path where that
  // provider doesn't exist.
  const filtersRef = useMemoizedSelector(
    makeRunSetFiltersRefSelector,
    runSetRefs[0]
  );
  const convertSelectionsToFilters = ViewHooks.useViewAction(
    filtersRef,
    FilterActions.selectionsToFilters
  );

  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});
  const panelQuery = useMemo(
    () => transformQuery(query, panel),
    [query, panel]
  );

  const runsDataQuery = useRunsData(panelQuery);

  return {
    convertSelectionsToFilters,
    customRunColors,
    customRunNames,
    data: runsDataQuery.data,
    loading: runsDataQuery.loading,
    pageQuery: query,
    query: panelQuery,
    semanticLegendSettings,
    updateConfig,
    runsDataQuery,
    config: panel.config,
  };
};
